import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from '../components/Button';
import { WorkflowSprawlDiagram } from '../components/Diagrams';
export function WhyNotHubspot() {
  return <div className="flex flex-col w-full bg-white" data-id="element-420">
      {/* Header */}
      <section className="pt-20 pb-16 bg-slate-50 border-b border-slate-200" data-id="element-421">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center" data-id="element-422">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl mb-6" data-id="element-423">
            Why not just use HubSpot workflows?
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto" data-id="element-424">
            HubSpot workflows are great for basic automation. But when lead
            routing gets complex, they become a tangled, unmaintainable mess.
          </p>
        </div>
      </section>

      {/* The Problem Visual */}
      <section className="py-16" data-id="element-425">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-426">
          <WorkflowSprawlDiagram data-id="element-427" />
        </div>
      </section>

      {/* Comparison Blocks */}
      <section className="py-16 bg-white" data-id="element-428">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 space-y-16" data-id="element-429">
          <div className="grid md:grid-cols-2 gap-8 items-start" data-id="element-430">
            <div className="bg-red-50/50 p-8 rounded-2xl border border-red-100" data-id="element-431">
              <div className="flex items-center gap-2 text-red-600 font-semibold mb-4" data-id="element-432">
                <AlertCircle size={20} data-id="element-433" /> The Workflow Way
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-434">
                Fragmented Logic
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed" data-id="element-435">
                You have one workflow for Demo Requests, another for Contact Us,
                and another for Content Downloads. When a territory changes, you
                have to update it in 5 different places. It's easy to miss one,
                leading to misrouted leads.
              </p>
            </div>
            <div className="bg-brand-50/50 p-8 rounded-2xl border border-brand-100" data-id="element-436">
              <div className="flex items-center gap-2 text-brand-600 font-semibold mb-4" data-id="element-437">
                <CheckCircle2 size={20} data-id="element-438" /> The DemandPath Way
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-439">
                Centralized Ruleset
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed" data-id="element-440">
                All inbound leads flow through a single, global routing engine.
                You define your territories and rules once. When a rep leaves or
                a territory shifts, you update it in one place and it applies
                everywhere instantly.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-start" data-id="element-441">
            <div className="bg-red-50/50 p-8 rounded-2xl border border-red-100" data-id="element-442">
              <div className="flex items-center gap-2 text-red-600 font-semibold mb-4" data-id="element-443">
                <AlertCircle size={20} data-id="element-444" /> The Workflow Way
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-445">
                Zero Visibility
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed" data-id="element-446">
                When a VP asks "Why did this enterprise lead go to a junior
                rep?", you have to dig through the contact's workflow history,
                guess which branch it took, and hope you can figure it out.
              </p>
            </div>
            <div className="bg-brand-50/50 p-8 rounded-2xl border border-brand-100" data-id="element-447">
              <div className="flex items-center gap-2 text-brand-600 font-semibold mb-4" data-id="element-448">
                <CheckCircle2 size={20} data-id="element-449" /> The DemandPath Way
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-450">
                Total Auditability
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed" data-id="element-451">
                DemandPath logs every single routing decision. You can see
                exactly which rule matched, what the lead's properties were at
                that exact moment, and why it was assigned to that specific rep.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-start" data-id="element-452">
            <div className="bg-red-50/50 p-8 rounded-2xl border border-red-100" data-id="element-453">
              <div className="flex items-center gap-2 text-red-600 font-semibold mb-4" data-id="element-454">
                <AlertCircle size={20} data-id="element-455" /> The Workflow Way
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-456">
                Basic Round Robin
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed" data-id="element-457">
                HubSpot's native rotate record action is basic. You can't easily
                weight it for new reps, you can't pause it when someone goes to
                lunch, and handling OOO requires manual intervention.
              </p>
            </div>
            <div className="bg-brand-50/50 p-8 rounded-2xl border border-brand-100" data-id="element-458">
              <div className="flex items-center gap-2 text-brand-600 font-semibold mb-4" data-id="element-459">
                <CheckCircle2 size={20} data-id="element-460" /> The DemandPath Way
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-461">
                Advanced Distribution
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed" data-id="element-462">
                True round robin with weighted distribution (e.g., give the new
                rep 50% fewer leads), working hours awareness, and easy OOO
                toggles so leads never sit with an unavailable rep.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-slate-50 border-t border-slate-200" data-id="element-463">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center" data-id="element-464">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl mb-6" data-id="element-465">
            Ready to move beyond brittle routing logic?
          </h2>
          <Button href="/book-a-demo" size="lg" data-id="element-466">
            See DemandPath in action
          </Button>
        </div>
      </section>
    </div>;
}